import type { TeamMemberStatistic } from '@api'
import type { ColumnDef } from '@tanstack/react-table'

import IconChevronRight from '@/assets/icons/icon-chevron-right.svg'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar.tsx'
import { Button } from '@/components/ui/button.tsx'
import { getSalary } from '@/modules/accounting/utils.ts'
import { roundAmount } from '@/utils/number.ts'

export const getMemberColumns = ({
  showCents,
  onSetSalary,
}: {
  showCents: boolean
  onSetSalary: (item: TeamMemberStatistic) => void
}): ColumnDef<TeamMemberStatistic>[] => [
  {
    accessorKey: 'username',
    header: 'Member',
    enableSorting: false,
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-2">
          <Avatar>
            <AvatarImage src={row.original.profilePicture} />
            <AvatarFallback>
              {row.original.username?.slice(0, 2)}
            </AvatarFallback>
          </Avatar>
          {row.original.username}
        </div>
      )
    },
  },
  {
    accessorKey: 'revenue',
    header: 'Total Revenue',
    sortingFn: 'auto',
    cell: ({ row }) => roundAmount(row.original.revenue, showCents),
  },
  {
    accessorKey: 'agencyProfit',
    header: 'Member Salary',
    sortingFn: (a, b) => {
      return getSalary(a.original) - getSalary(b.original)
    },
    cell: ({ row }) => {
      const salary = getSalary(row.original)

      return roundAmount(salary, showCents) ?? '—'
    },
  },
  {
    accessorKey: 'cut',
    header: 'My Cut',
    sortingFn: 'auto',
    cell: ({ row }) => roundAmount(row.original.agencyProfit, showCents),
  },
  {
    accessorKey: 'fixedSalary',
    header: 'Fixed Salary',
    sortingFn: (a, b) => {
      return (
        (a.original.compensation?.fixedSalary ?? 0) -
        (b.original.compensation?.fixedSalary ?? 0)
      )
    },
    cell: ({ row }) => (
      <span data-testid="fixed-salary-value">
        {roundAmount(row.original.compensation?.fixedSalary, showCents)}
      </span>
    ),
  },
  {
    accessorKey: 'cutOfRevenue',
    header: 'Percentage Cut of Revenue',
    sortingFn: (a, b) => {
      return (
        (a.original.compensation?.cutOfRevenuePercentage ?? 0) -
        (b.original.compensation?.cutOfRevenuePercentage ?? 0)
      )
    },
    cell: ({ row }) => (
      <span data-testid="cut-of-revenue-value">{`${row.original.compensation?.cutOfRevenuePercentage ?? 0}%`}</span>
    ),
  },
  {
    id: 'actions',
    // @ts-ignore
    header: <div className="w-full text-right">Action</div>,
    cell: function ActionCell({ row }) {
      return (
        <div className="flex justify-end">
          <Button
            onClick={() => onSetSalary(row.original)}
            variant="ghost"
            className="p-0"
            testId="set-salary-button"
          >
            Set Salary <IconChevronRight />
          </Button>
        </div>
      )
    },
  },
]
